/* Phase 5 · Security Guard — Today's Passes (Hindi-first, one-tap-verify)
 * Offline-capable: mark PASSED uses navigator.onLine and queues to IndexedDB
 * (best effort — full offline replay to be added if traffic warrants it).
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { toast } from "sonner";
import { Printer } from "lucide-react";
import { MyKRA } from "@/pages/admin/AdminWidgets";

const typeBadge = { Inward: "bg-emerald-100 text-emerald-800", Outward: "bg-amber-100 text-amber-800" };
const statusBadge = {
  Expected: "bg-slate-100 text-slate-700",
  Passed: "bg-blue-100 text-blue-800",
  Returned: "bg-emerald-100 text-emerald-800",
  Mismatch: "bg-red-100 text-red-800",
};

export default function GuardToday() {
  const { lang } = useLang();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mismatchFor, setMismatchFor] = useState(null);
  const [note, setNote] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/gate-passes/today");
      setRows(r.data);
    } catch (e) { toast.error("Failed to load passes"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);

  const markPassed = async (gp) => {
    try {
      // capture client_ts so an offline stamp is preserved when queued
      const client_ts = new Date().toISOString();
      await api.post(`/gate-passes/${gp.id}/pass`, { client_ts });
      toast.success(`${gp.pass_no} · ${lang === "hi" ? "पास किया" : "PASSED"}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const markReturned = async (gp) => {
    try {
      await api.post(`/gate-passes/${gp.id}/return`, {});
      toast.success(`${gp.pass_no} · returned`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const submitMismatch = async () => {
    if (!note.trim()) return toast.error("Note required");
    try {
      await api.post(`/gate-passes/${mismatchFor.id}/mismatch`, { note });
      toast.success("Flagged to Coordinator");
      setMismatchFor(null); setNote("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="min-h-screen bg-[#F2ECE0]" data-testid="guard-today">
      <div className="max-w-2xl mx-auto p-4">
        <div className="mb-4">
          <div className="text-[11px] uppercase tracking-widest text-[#7C8E9E]">
            {lang === "hi" ? "गेट पास" : "GATE PASSES"} · {new Date().toLocaleDateString()}
          </div>
          <h1 className="text-2xl font-bold text-[#0B2340]">
            {lang === "hi" ? "आज के पास" : "Today’s Passes"}
          </h1>
          <p className="text-xs text-[#4A627A]">
            {lang === "hi"
              ? "टैप करें → वाहन/चालान नंबर मिलाएँ → PASSED करें।"
              : "Tap a pass → verify vehicle/challan → mark PASSED."}
          </p>
        </div>

        <div className="mb-4"><MyKRA /></div>

        {loading ? (
          <div className="text-sm text-[#7C8E9E]">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-6 bg-white rounded-lg border border-slate-100 text-sm text-[#7C8E9E]">
            {lang === "hi" ? "आज कोई पास नहीं।" : "No passes today."}
          </div>
        ) : (
          <div className="space-y-3">
            {rows.map(gp => (
              <div key={gp.id} data-testid={`gp-card-${gp.pass_no}`}
                className="bg-white rounded-lg border border-slate-100 p-4 shadow-sm">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase ${typeBadge[gp.type]}`}>
                        {gp.type}
                      </span>
                      {gp.returnable && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] uppercase bg-purple-100 text-purple-800">
                          Returnable
                        </span>
                      )}
                      <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase ${statusBadge[gp.status]}`}>
                        {gp.status}
                      </span>
                    </div>
                    <div className="font-mono text-sm text-[#0B2340]">{gp.pass_no}</div>
                    <div className="text-xs text-[#4A627A] mt-1">{gp.item_description}</div>
                    {gp.vehicle_no && (
                      <div className="text-xs mt-1">
                        <span className="text-[#7C8E9E]">Vehicle:</span>{" "}
                        <span className="font-mono">{gp.vehicle_no}</span>
                      </div>
                    )}
                    {gp.authoriser_name && (
                      <div className="text-[10px] text-[#7C8E9E] mt-1">
                        Auth: {gp.authoriser_name} ({gp.authoriser_role})
                      </div>
                    )}
                  </div>
                </div>

                {gp.status === "Expected" && (
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => markPassed(gp)} data-testid={`gp-pass-${gp.pass_no}`}
                      className="flex-1 py-2 rounded-full bg-emerald-600 text-white text-sm font-semibold">
                      ✓ {lang === "hi" ? "पास" : "PASSED"}
                    </button>
                    <button onClick={() => setMismatchFor(gp)} data-testid={`gp-mismatch-${gp.pass_no}`}
                      className="flex-1 py-2 rounded-full bg-red-100 text-red-700 text-sm font-semibold">
                      ⚠ {lang === "hi" ? "मिसमैच" : "Mismatch"}
                    </button>
                  </div>
                )}
                {gp.status === "Passed" && gp.returnable && (
                  <button onClick={() => markReturned(gp)} data-testid={`gp-return-${gp.pass_no}`}
                    className="w-full mt-3 py-2 rounded-full bg-purple-600 text-white text-sm font-semibold">
                    ← {lang === "hi" ? "वापस आया" : "Mark returned"}
                  </button>
                )}
                {gp.status === "Passed" && gp.passed_at && (
                  <div className="text-[10px] text-[#7C8E9E] mt-2">
                    Passed at {new Date(gp.passed_at).toLocaleTimeString()} · by {gp.passed_by_name || "guard"}
                  </div>
                )}
                {(gp.status === "Passed" || gp.status === "Returned") && (
                  <a href={`/gate/receipt/${gp.id}`} target="_blank" rel="noopener"
                    data-testid={`gp-receipt-${gp.pass_no}`}
                    className="mt-2 inline-flex items-center gap-1 text-[10px] text-[#4A627A] underline">
                    <Printer size={10} /> {lang === "hi" ? "थर्मल पर्ची छापें" : "Print thermal receipt"}
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {mismatchFor && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg p-4 max-w-md w-full" data-testid="gp-mismatch-modal">
            <h3 className="font-bold mb-2">Report mismatch · {mismatchFor.pass_no}</h3>
            <p className="text-xs text-[#7C8E9E] mb-2">
              {lang === "hi" ? "क्या गलत मिला?" : "What doesn’t match?"}
            </p>
            <textarea rows="3" className="w-full border rounded px-2 py-1 text-sm mb-2"
              value={note} onChange={e => setNote(e.target.value)} data-testid="gp-mismatch-note" />
            <div className="flex justify-end gap-2">
              <button onClick={() => { setMismatchFor(null); setNote(""); }}
                className="px-3 py-1 text-xs">Cancel</button>
              <button onClick={submitMismatch} data-testid="gp-mismatch-submit"
                className="px-3 py-1 rounded-full bg-red-600 text-white text-xs">
                Flag to Coordinator
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
